import { useEffect, useRef, useState, type FormEvent, type MouseEvent, type PointerEvent, type ReactNode, type Ref } from "react";

type ThemeInfo = { name: string; color: string; icon: string };

export function ThemeStrip({ theme, timer, actions }: { theme: ThemeInfo; timer?: ReactNode; actions?: ReactNode }) {
  return (
    <div className="play-theme shrink-0 z-30 grid gap-2 py-2">
      <div className="flex items-center justify-between gap-3 min-w-0">
        <span
          className="rounded-full px-3 py-1 text-sm font-semibold min-w-0 break-words leading-snug"
          style={{ background: theme.color, color: "#160d1c" }}
        >
          {theme.icon} {theme.name}
        </span>
        {timer ? <span className="shrink-0">{timer}</span> : null}
      </div>
      {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
    </div>
  );
}

export function AnswerDock({
  value,
  onChange,
  onSubmit,
  locked,
  listenLock,
  listenLeft = 0,
  retryLock,
  retryLeft = 0,
  keepOpen = true,
  placeholder = "Ta réponse",
  submitLabel = "Valider",
  extra,
  message,
  inputRef,
}: {
  value: string;
  onChange: (next: string) => void;
  onSubmit: () => void | Promise<void>;
  locked?: boolean;
  listenLock?: boolean;
  listenLeft?: number;
  retryLock?: boolean;
  retryLeft?: number;
  keepOpen?: boolean;
  placeholder?: string;
  submitLabel?: string;
  extra?: ReactNode;
  message?: string;
  inputRef?: Ref<HTMLInputElement>;
}) {
  const localRef = useRef<HTMLInputElement | null>(null);
  const dockRef = useRef<HTMLDivElement>(null);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (!value.trim()) {
      setSent(false);
      setSending(false);
    }
  }, [value]);

  function setRefs(el: HTMLInputElement | null) {
    localRef.current = el;
    if (!inputRef) return;
    if (typeof inputRef === "function") inputRef(el);
    else inputRef.current = el;
  }

  useEffect(() => {
    if (!keepOpen) return;
    const focus = () => localRef.current?.focus({ preventScroll: true });
    focus();
    const onBlur = () => {
      window.setTimeout(focus, 0);
    };
    const el = localRef.current;
    el?.addEventListener("blur", onBlur);
    return () => el?.removeEventListener("blur", onBlur);
  }, [keepOpen]);

  useEffect(() => {
    const vv = window.visualViewport;
    const dock = dockRef.current;
    if (!vv || !dock) return;
    const update = () => {
      const inset = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      dock.style.paddingBottom = `${Math.max(inset, 8)}px`;
    };
    update();
    vv.addEventListener("resize", update);
    vv.addEventListener("scroll", update);
    return () => {
      vv.removeEventListener("resize", update);
      vv.removeEventListener("scroll", update);
    };
  }, []);

  function submit(e?: FormEvent) {
    e?.preventDefault();
    if (locked || listenLock || retryLock || sending) return;
    if (!value.trim()) return;
    setSending(true);
    setSent(false);
    void Promise.resolve(onSubmit())
      .catch(() => undefined)
      .finally(() => {
        setSending(false);
        setSent(true);
        window.setTimeout(() => localRef.current?.focus({ preventScroll: true }), 0);
      });
  }

  function holdFocus(e: PointerEvent<HTMLButtonElement> | MouseEvent<HTMLButtonElement>) {
    e.preventDefault();
  }

  return (
    <div ref={dockRef} className="play-dock shrink-0 z-30 pt-2">
      <form onSubmit={submit} className="grid gap-2">
        <input
          ref={setRefs}
          value={value}
          onChange={(e) => {
            if (locked || listenLock) return;
            setSent(false);
            onChange(e.target.value);
          }}
          onBeforeInput={(e) => {
            if (locked || listenLock) e.preventDefault();
          }}
          onPaste={(e) => {
            if (locked || listenLock) e.preventDefault();
          }}
          onDrop={(e) => {
            if (locked || listenLock) e.preventDefault();
          }}
          onKeyDown={(e) => {
            if ((locked || listenLock) && e.key !== "Tab") e.preventDefault();
          }}
          className={`w-full rounded-2xl bg-cream text-ink text-xl px-4 py-3 ${locked || listenLock ? "opacity-70" : ""}`}
          placeholder={listenLock ? `Écoute… encore ${Math.max(1, listenLeft)}s` : placeholder}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          enterKeyHint="go"
          inputMode="text"
        />
        {listenLock && !message && (
          <p className="text-gold text-center text-sm font-semibold">Écoute les 2 premières secondes</p>
        )}
        {retryLock && !listenLock && (
          <p className="text-gold text-center text-sm font-semibold">
            Réessaie dans {Math.max(1, retryLeft)}s
          </p>
        )}
        {sending && <p className="text-mint text-center text-sm font-semibold">Envoi en cours…</p>}
        {!sending && sent && !message && <p className="text-mint text-center text-sm font-semibold">Réponse envoyée</p>}
        {message && <p className="text-pink text-center text-sm">{message}</p>}
        <div className={`grid gap-2 ${extra ? "grid-cols-2" : "grid-cols-1"}`}>
          <button
            type="submit"
            disabled={locked || listenLock || retryLock || sending}
            onMouseDown={holdFocus}
            onPointerDown={holdFocus}
            className="rounded-2xl bg-gold text-ink font-bold py-3 disabled:opacity-50"
          >
            {sending ? "Envoi…" : submitLabel}
          </button>
          {extra}
        </div>
      </form>
    </div>
  );
}
